/**
 * Parse display-formatted percentage text back to an Excel percentage, for the
 * Excel export — the percent twin of `parseCurrencyText` in ./money.
 *
 * Percent cells reach the exporter as display TEXT ("98%", "1,250.5%", "-5%"),
 * which lands in Excel as a string: it cannot be formatted as a percentage,
 * averaged, or referenced as a number by a downstream sheet. Returning a number
 * lets the exporter write a real numeric cell with a percent number format.
 *
 * `value` is the EXCEL representation — the displayed number divided by 100, so
 * 0.98 under a "0%" format renders as "98%", which is what Excel means by a
 * percentage. `decimals` is how many fraction digits the text carried, so the
 * caller can pick a number format that shows the same precision.
 *
 * Anything that is not wholly a percentage returns null and stays text: a bare
 * number ("98"), a value with trailing words ("50% off"), or a cell that
 * combines a percent with other text ("96%, 150/156" — the Show-details cells).
 */
export function parsePercentText(text: string): { value: number; decimals: number } | null {
  const s = text.trim();
  const m = /^([-+])?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d+))?\s*%$/.exec(s);
  if (!m) return null;
  const [, sign, intPart, frac] = m;
  const n = Number(intPart.replace(/,/g, '') + (frac ? `.${frac}` : ''));
  if (!Number.isFinite(n)) return null;
  return { value: (sign === '-' ? -n : n) / 100, decimals: frac ? frac.length : 0 };
}

/**
 * Excel number format for a percentage carrying `decimals` fraction digits:
 * 0 -> "0%", 1 -> "0.0%". Capped at 6 so a repeating ratio that rendered as
 * "33.333333%" cannot build an absurd format string.
 */
export function percentNumFmt(decimals: number): string {
  const d = Math.min(Math.max(decimals, 0), 6);
  return d > 0 ? `0.${'0'.repeat(d)}%` : '0%';
}
